import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { RouteInstancesFilter } from './route-instances-filter/route-instances-filter';
import { RouteInstancesService } from '../../../../../services/route/route-instance-service';
import {
  RouteInstanceFilters,
  RouteInstanceItem,
} from '../../../../../pages/admin-dashboard/admin-route-instance-management/route-instances.model';
import { LoadState, RouteInstancesTable } from './route-instances-table/route-instances-table';

@Component({
  selector: 'app-routes-instances-list-component',
  standalone: true,
  imports: [RouteInstancesFilter, RouteInstancesTable],
  templateUrl: './routes-instances-list-component.html',
  styleUrl: './routes-instances-list-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoutesInstancesListComponent implements OnInit {
  private routeInstancesService = inject(RouteInstancesService);
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);

  startRouteInstanceError = signal('');
  cancelRouteError = signal('');
  state = signal<LoadState>('loading');
  instances = signal<RouteInstanceItem[] | null>(null);
  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(25);
  totalItems = signal<number>(0);
  isFiltersApplied = signal<boolean>(false);
  closeStartDialog = signal(0);
  filters = signal<RouteInstanceFilters>({
    orderBy: 'LAST_MODIFICATION_DATE',
    orderDir: 'DESC',
  });

  ngOnInit(): void {
    this.loadInstances();
  }

  loadInstances() {
    this.filters.update((f) => ({
      ...f,
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
    }));
    this.state.set('loading');

    const sub = this.routeInstancesService.getAllRouteInstances(this.filters()).subscribe({
      next: (res) => {
        this.instances.set(res.list);
        this.totalItems.set(res.total);
        this.state.set('success');
      },
      error: () => this.state.set('error'),
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  onCancelRouteInstance(event: { id: number; comment: string }) {
    this.cancelRouteError.set('');

    const sub = this.routeInstancesService.cancelRouteInstance(event.id, event.comment).subscribe({
      next: () => {
        this.cancelRouteError.set('');
        this.loadInstances();
      },
      error: (error) => {
        const apiError = error.error;
        const message = apiError?.reqBodyErrors?.length
          ? apiError.reqBodyErrors.join('\n')
          : this.isArabic()
            ? apiError?.messageAr || apiError?.messageEn || 'حدث خطأ أثناء إلغاء وردية خط السير.'
            : apiError?.messageEn || apiError?.messageAr || 'An error occurred while canceling the route instance.';

        this.cancelRouteError.set(message);
      },
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems() / this.itemsPerPage());
  }

  toggleSortOrder() {
    this.filters.update((f) => ({
      ...f,
      orderDir: f.orderDir === 'ASC' ? 'DESC' : 'ASC',
    }));
    this.loadInstances();
  }

  applyFilters(applied: RouteInstanceFilters) {
    this.currentPage.set(1);
    this.isFiltersApplied.set(true);
    this.filters.update((f) => ({ ...f, ...applied, pageNum: 0, pageSize: this.itemsPerPage() }));
    this.loadInstances();
  }

  resetFilters() {
    this.filters.set({ orderBy: 'LAST_MODIFICATION_DATE', orderDir: 'DESC' });
    this.isFiltersApplied.set(false);
    this.currentPage.set(1);
    this.loadInstances();
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
      this.loadInstances();
    }
  }

  onPageSizeChange(size: number) {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
    this.loadInstances();
  }

  navigateToDetails(id: number) {
    this.router.navigate(['/admin/route-instances/view-route-instance', id]);
  }

  isArabic = computed(() => String(this.locale).startsWith('ar'));

  onStartRouteInstance(event: { id: number; comment: string }) {
    this.startRouteInstanceError.set('');

    const sub = this.routeInstancesService.startRouteInstance(event.id, event.comment).subscribe({
      next: () => {
        this.startRouteInstanceError.set('');
        this.closeStartDialog.update((value) => value + 1);
        this.loadInstances();
      },
      error: (error) => {
        const apiError = error.error;
        const message = apiError?.reqBodyErrors?.length
          ? apiError.reqBodyErrors.join('\n')
          : this.isArabic()
            ? apiError?.messageAr || apiError?.messageEn || 'حدث خطأ أثناء بدء الوردية.'
            : apiError?.messageEn || apiError?.messageAr || 'An error occurred while starting the route instance.';

        this.startRouteInstanceError.set(message);
      },
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  @HostListener('document:click')
  onDocumentClick() {}
}
